import { Delete } from '@interhyp/platform-icons';
import _ from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { MaybeWrapper } from "../../helper/components/MaybeWrapper.js";
import { ErrorMessage } from "../../helper/components/errorMessage/ErrorMessage.js";
import { AddButton } from "../addButton/AddButton.js";
import { Button } from "../button/Button.js";
import { IconButton } from "../iconButton/IconButton.js";
import { ModalContent } from "../modal/components/modalContent/ModalContent.js";
import { ModalFooter } from "../modal/components/modalFooter/ModalFooter.js";
import { Typography } from "../typography/Typography.js";
import styles from "./FormList.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function FormList({
  rows,
  onSave,
  columnTitles,
  FormRow,
  defaultObject = {},
  validation,
  insideModal,
  className,
  hideAdd,
  hideDelete,
  addTitle = 'Hinzufügen',
  deleteTitle = 'Entfernen',
  generalErrorMessage = 'Bitte füllen Sie alle notwendigen Felder aus.',
  rowTitleGenerator = standardRowTitleGenerator,
  ...otherProps
}) {
  const [localRows, setLocalRows] = useState(addIds(rows));
  const [validationErrors, setValidationResults] = useState([]);
  // Disable button initially because at least one change should be done
  // This disable button behavior is a special case for the FormList component => DO NOT COPY THIS BEHAVIOR FOR OTHER FORMS
  // Ask the UX chapter for more information
  const [saveDisabled, setSaveDisabled] = useState(true);
  useEffect(() => {
    setLocalRows(addIds(rows));
  }, [rows]);

  // Execute validation function for each row
  const validate = useCallback(() => {
    const newValidationResults = localRows.map(validation);
    setValidationResults(newValidationResults);
    return !invalidRowsCheck(newValidationResults);
  }, [validation, localRows]);
  const hasInvalidRows = useMemo(() => invalidRowsCheck(validationErrors), [validationErrors]);

  // Re-validate when localRows change
  useEffect(() => {
    // Only trigger validate if there are invalid rows (otherwise it will be triggered initial)
    if (hasInvalidRows) {
      validate();
    }
  }, [hasInvalidRows, validate]);
  const setField = useCallback((index, field, newFieldValue) => {
    setSaveDisabled(false);
    setLocalRows(currentLocalValue => {
      if (!currentLocalValue) {
        return [];
      }
      const newLocalValue = [...currentLocalValue];
      newLocalValue[index][field] = newFieldValue;
      return newLocalValue;
    });
  }, [setLocalRows]);
  const removeRow = useCallback(index => {
    setSaveDisabled(false);
    setLocalRows(currentLocalValue => {
      const newLocalValue = [...currentLocalValue];
      newLocalValue.splice(index, 1);
      return newLocalValue;
    });
  }, [setLocalRows]);
  const addRow = useCallback(() => {
    setSaveDisabled(false);
    setLocalRows(currentLocalValue => {
      return addIds([...(currentLocalValue ?? []), cloneDeep(defaultObject)]);
    });
  }, [setLocalRows, defaultObject]);
  const handleSave = useCallback(() => {
    if (validate()) {
      onSave(
      // remove unused id
      localRows.map(({
        __localRowId,
        ...otherProps
      }) => {
        // needs type cast here because all props are validated through the validation function
        return otherProps;
      }));
    }
  }, [localRows, validate, onSave]);
  return /*#__PURE__*/_jsxs("div", {
    className: _(styles.formList, className),
    style: {
      '--row-columns': `${columnTitles.length}`
    },
    children: [/*#__PURE__*/_jsxs(MaybeWrapper, {
      condition: insideModal,
      wrapper: c => /*#__PURE__*/_jsx(ModalContent, {
        children: c
      }),
      children: [/*#__PURE__*/_jsxs("div", {
        className: _(styles.row, styles.first, !hideDelete && styles.rowWithDeleteBtn),
        children: [columnTitles.map((title, index) => /*#__PURE__*/_jsx(Typography, {
          variant: "strong",
          margin: false,
          children: title
        }, index)), /*#__PURE__*/_jsx("div", {})]
      }), localRows?.map((rowData, index) => {
        return /*#__PURE__*/_jsxs("div", {
          className: _(styles.row, !hideDelete && styles.rowWithDeleteBtn),
          children: [!hideDelete && /*#__PURE__*/_jsxs("div", {
            className: styles.mobileDeleteBtnRow,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "strong",
              children: rowTitleGenerator(index + 1)
            }), /*#__PURE__*/_jsx(IconButton, {
              title: deleteTitle,
              onClick: () => removeRow(index),
              icon: Delete
            })]
          }), /*#__PURE__*/_jsx(FormRow, {
            data: rowData,
            metaData: {
              localRowsLength: localRows.length,
              validationErrors
            },
            index: index,
            setField: setField,
            validationError: validationErrors[index]
          }), !hideDelete && /*#__PURE__*/_jsx("div", {
            className: styles.deleteBtn,
            children: /*#__PURE__*/_jsx(IconButton, {
              title: deleteTitle,
              onClick: () => removeRow(index),
              icon: Delete
            })
          })]
        }, rowData.__localRowId);
      }), !hideAdd && /*#__PURE__*/_jsxs("div", {
        className: _(styles.row, styles.last),
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.addBtnDesktop,
          children: /*#__PURE__*/_jsx(AddButton, {
            name: addTitle,
            onClick: addRow,
            variant: "CONDENSED"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.addBtnMobile,
          children: /*#__PURE__*/_jsx(AddButton, {
            name: addTitle,
            onClick: addRow,
            variant: "HORIZONTAL_BOX"
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(MaybeWrapper, {
      condition: insideModal,
      wrapper: c => /*#__PURE__*/_jsx(ModalFooter, {
        actions: [...('modalActions' in otherProps ? otherProps.modalActions ?? [] : []), {
          semantic: 'PRIMARY',
          label: 'Speichern',
          handler: handleSave,
          disabled: saveDisabled
        }],
        children: c
      }),
      children: [hasInvalidRows && generalErrorMessage !== false && /*#__PURE__*/_jsx(ErrorMessage, {
        className: styles.errorMessage,
        children: generalErrorMessage
      }), !insideModal && /*#__PURE__*/_jsx("div", {
        className: styles.saveRow,
        children: /*#__PURE__*/_jsx(Button, {
          disabled: saveDisabled,
          onClick: handleSave,
          children: "Speichern"
        })
      })]
    })]
  });
}

/**
 * Add IDs to local value because otherwise only the index is used as key and react can't update the ui correctly
 * @param objects
 */
function addIds(objects) {
  return objects?.map(o => {
    if (hasId(o)) {
      return o;
    }
    return {
      ...o,
      __localRowId: `list-row_${v4()}`
    };
  });
}
function hasId(someObject) {
  return someObject.__localRowId !== undefined;
}
function invalidRowsCheck(validationRows) {
  return validationRows.some(f => f !== undefined);
}
function standardRowTitleGenerator(rowIndex) {
  return `${rowIndex}.`;
}